import type { GuideStep } from '../types';

export const BLOCK_STEPS: GuideStep[] = [
  {
    id: 'blocks-1a',
    page: 'index',
    section: 'blocks',
    target: 'tool-sidebar',
    title: { sv: 'Verktygspanelen', en: 'The tool sidebar' },
    body: {
      sv: 'Här finns alla blocktyper du kan lägga på canvasen. Låt oss prova att dra ut ett.',
      en: 'All block types live here in the sidebar. Let\'s drag one out onto the canvas.',
    },
    placement: 'right',
    order: 1,
  },
  {
    id: 'blocks-1',
    page: 'index',
    section: 'blocks',
    target: 'tool-sidebar-items',
    title: { sv: 'Dra ett verktyg till canvasen', en: 'Drag a tool onto the canvas' },
    body: {
      sv: 'Ta tag i ett av verktygen och släpp det på canvasen. Du kan också dubbelklicka på ett verktyg — då landar det mitt i vyn.',
      en: 'Grab one of the tools and drop it on the canvas. You can also double-click a tool to drop it in the center.',
    },
    advanceOn: { kind: 'drop' },
    showMeHow: {
      steps: [
        { selector: '[data-guide="tool-sidebar-items"] > div > div:nth-child(1)', action: 'press', label: 'Press tool', hold: 400 },
        { selector: '[data-guide="canvas"]', action: 'release', label: 'Drop on canvas', hold: 600 },
      ],
      durationMs: 3500,
    },
    placement: 'right',
    order: 2,
  },
  {
    id: 'blocks-2',
    page: 'index',
    section: 'blocks',
    target: 'block',
    title: { sv: 'Flytta blocket', en: 'Move the block' },
    body: {
      sv: 'Klicka och dra blocket för att flytta det. Varje block har 8 anslutningsportar — uppe, nere, sidorna och diagonalerna.',
      en: 'Click and drag the block to move it. A block has 8 connection ports — top, bottom, sides and diagonals.',
    },
    advanceOn: { kind: 'move', target: 'block' },
    showMeHow: {
      steps: [
        { selector: '[data-guide="block"]::eq(0)', action: 'press', label: 'Click & hold', hold: 400 },
        { selector: '[data-guide="canvas"]', action: 'release', label: 'Drag, then release', hold: 600 },
      ],
      durationMs: 3500,
    },
    placement: 'auto',
    order: 3,
  },
  {
    id: 'blocks-3pre',
    page: 'index',
    section: 'blocks',
    target: 'block',
    fallbackTargets: ['layer-card', 'canvas'],
    mobileTarget: null,
    title: { sv: 'Markera blocket', en: 'Select the block' },
    body: {
      sv: 'Klicka på det highlightade blocket. Egenskapspanelen öppnas automatiskt så vi kan titta på inställningarna.',
      en: 'Click the highlighted block. The properties panel opens automatically so we can look at the settings.',
    },
    advanceOn: { kind: 'click' },
    showMeHow: {
      steps: [
        { selector: '[data-guide="block"], [data-guide="layer-card"]', action: 'click', label: 'Click', hold: 700 },
      ],
      durationMs: 2500,
    },
    placement: 'auto',
    order: 3.5,
  },
  {
    id: 'blocks-3',
    page: 'index',
    section: 'blocks',
    target: 'properties',
    mobileTarget: null,
    title: { sv: 'Egenskapspanelen', en: 'The properties panel' },
    body: {
      sv: 'Bra! Nu ser du blockets inställningar här till höger. Vi går igenom fälten.',
      en: "Nice! You can see the block's settings here on the right. Let's walk through them.",
    },
    placement: 'left',
    order: 4,
  },
  {
    id: 'blocks-3a',
    page: 'index',
    section: 'blocks',
    target: 'properties-name',
    fallbackTargets: ['properties'],
    mobileTarget: null,
    title: { sv: 'Namn', en: 'Name' },
    body: {
      sv: 'Skriv ett tydligt namn — det visas direkt på blocket på canvasen.',
      en: "Type a clear name — it shows on the block on the canvas.",
    },
    placement: 'left',
    order: 5,
  },
  {
    id: 'blocks-3b',
    page: 'index',
    section: 'blocks',
    target: 'properties-description',
    fallbackTargets: ['properties'],
    mobileTarget: null,
    title: { sv: 'Beskrivning', en: 'Description' },
    body: {
      sv: 'Lägg till en beskrivning som förklarar vad blocket gör. AI-assistenten använder den för att förstå sammanhanget.',
      en: 'Add a description explaining what the block does. The AI assistant uses it for context.',
    },
    placement: 'left',
    order: 6,
  },
  {
    id: 'blocks-3c',
    page: 'index',
    section: 'blocks',
    target: 'properties-color',
    fallbackTargets: ['properties'],
    mobileTarget: null,
    title: { sv: 'Färg', en: 'Color' },
    body: {
      sv: 'Färga blocket för att gruppera visuellt. Klicka på rutan eller skriv en hex-kod.',
      en: 'Color the block to group it visually. Click the swatch or type a hex code.',
    },
    placement: 'left',
    order: 7,
  },
  {
    id: 'blocks-3d',
    page: 'index',
    section: 'blocks',
    target: 'properties-actions',
    fallbackTargets: ['properties'],
    mobileTarget: null,
    title: { sv: 'Spara eller ta bort', en: 'Save or delete' },
    body: {
      sv: 'Klicka Spara för att applicera ändringarna eller papperskorgen för att ta bort blocket.',
      en: 'Click Save to apply changes or the trash icon to delete the block.',
    },
    placement: 'left',
    order: 8,
  },
  {
    id: 'blocks-4',
    page: 'index',
    section: 'blocks',
    target: 'canvas',
    title: { sv: 'Ta bort block', en: 'Delete a block' },
    body: {
      sv: 'Markera ett block och tryck Delete. Du kan alltid ångra med Ctrl+Z (50 steg).',
      en: 'Select a block and press Delete. You can always undo with Ctrl+Z (50 steps of history).',
    },
    placement: 'auto',
    order: 9,
  },
  {
    id: 'blocks-5',
    page: 'index',
    section: 'blocks',
    target: 'block',
    fallbackTargets: ['layer-card'],
    mobileTarget: null,
    includeOpenMenus: true,
    title: { sv: 'Högerklicks-meny', en: 'Right-click menu' },
    body: {
      sv: 'Högerklicka på ett block (eller lager-kort) för snabbåtgärder: döp om, beskrivning, färg, gruppera, konvertera till lager, duplicera och ta bort. Välj ett alternativ för att fortsätta. Du kan också högerklicka på tom canvas för att lägga till nya block.',
      en: 'Right-click a block (or layer card) for quick actions: rename, description, color, group, convert to layer, duplicate and delete. Pick any option to continue. You can also right-click empty canvas to add new blocks.',
    },
    // Advance on any menu item selection — forces the user to actually USE
    // the menu rather than just dismissing it, so the step is pedagogical.
    advanceOn: { kind: 'menuItemSelected' },
    showMeHow: {
      // Triggers a real contextmenu so the user sees what the menu
      // looks like. Escape is dispatched on cleanup to close it; the
      // user then performs their own right-click to pick an action.
      steps: [
        { selector: '[data-guide="block"]::eq(0)', action: 'rightclick', label: 'Right-click', hold: 1200, trigger: true },
      ],
      durationMs: 2500,
    },
    waitingHint: {
      sv: 'Högerklicka ett block och välj ett alternativ',
      en: 'Right-click a block and pick an option',
    },
    placement: 'auto',
    order: 10,
  },
];
